import { useState, useEffect } from 'react';
import _ from 'lodash';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import { useDebounce } from '@uidotdev/usehooks';
import { Settings, ViewerBoardInfo } from '@gorilla/widgets-viewer/app/viewer/types';
import { getSubscription } from '@gorilla/common/src/lib/monday-api/api';
import { getNormalizedBoardFiltersGraphQL } from '@gorilla/common/src/lib/rules';
import { enqueueSnackbar } from 'notistack';
import { useItemLoader } from './use-item-loader';
import { boardToBoardInfo, DEFAULT_SETTINGS } from '@gorilla/widgets-shared/src/services/publish';
import { mondayClient } from '../services/monday';
import { getSettings, saveSettings, getExecutions } from '../services/settings';
import { getDemoSettings } from '../services/demo-settings';
import { getDefaultSettings, findFirstFieldWithType } from '../services/default-settings';
import { getWidgetInfo, unpublishWidget } from '../services/widgets';
import { useMondayContext } from './use-monday-context';
import { useBoard } from './use-board';

function extractErrorMessage(error: any, fallback = 'An unknown error occurred'): string {
  if (_.get(error, 'error.name') === 'ZodError') {
    let message = _.get(error, 'error.issues[0].message');

    if (!message) {
      return fallback;
    }

    let path = _.get(error, 'error.issues[0].path');

    if (path && path.length) {
      message += ` (${path.join(', ')})`;
    }

    return message;
  }

  if (_.isString(_.get(error, 'message'))) {
    return _.get(error, 'message');
  }

  return fallback;
}

// metadata that lives in the settings blob but isn't a rendering setting, so it isn't in
// DEFAULT_SETTINGS and would otherwise be stripped by cleanSettings on every load
const NON_RENDERING_SETTINGS = ['name'];

function cleanSettings(settings: Settings) {
  // remove keys that are not part of DEFAULT_SETTINGS
  const newSettings: Settings = {
    ...settings,
  };

  for (const key of Object.keys(newSettings)) {
    if (!(key in DEFAULT_SETTINGS) && !NON_RENDERING_SETTINGS.includes(key)) {
      delete newSettings[key as keyof Settings];
    }
  }

  return newSettings;
}

function fixSettings(settings: Settings, boardInfo: ViewerBoardInfo) {
  const newSettings: Settings = {
    ...settings,
  };

  function columnNeedsFixing(columnId: string | null) {
    // column was removed from the board
    return !!(columnId && !boardInfo.fields.find((field) => field.id === columnId));
  }

  if (columnNeedsFixing(newSettings.gridItemImageColumn)) {
    newSettings.gridItemImageColumn = findFirstFieldWithType(boardInfo, ['file']);
  }

  if (columnNeedsFixing(newSettings.gridItemHeadlineColumn)) {
    newSettings.gridItemHeadlineColumn = 'name';
  }

  if (columnNeedsFixing(newSettings.eventsItemImageColumn)) {
    newSettings.eventsItemImageColumn = findFirstFieldWithType(boardInfo, ['file']);
  }

  if (columnNeedsFixing(newSettings.eventsItemHeadlineColumn)) {
    newSettings.eventsItemHeadlineColumn = 'name';
  }

  if (columnNeedsFixing(newSettings.eventsItemDateColumn)) {
    newSettings.eventsItemDateColumn = findFirstFieldWithType(boardInfo, ['date']);
  }

  if (columnNeedsFixing(newSettings.eventsItemLocationColumn)) {
    newSettings.eventsItemLocationColumn = findFirstFieldWithType(boardInfo, ['location']);
  }

  if (columnNeedsFixing(newSettings.eventsItemDescriptionColumn)) {
    newSettings.eventsItemDescriptionColumn = findFirstFieldWithType(boardInfo, ['long_text', 'text']);
  }

  if (columnNeedsFixing(newSettings.eventsItemLearnMoreColumn)) {
    newSettings.eventsItemLearnMoreColumn = findFirstFieldWithType(boardInfo, ['link']);
  }

  return cleanSettings(newSettings);
}

// the widget being edited. a board can hold several, so every request and every SWR cache key below
// has to be scoped to this widget rather than to the board.
export type EditorWidget = {
  id: string;
  path: string;
  name: string | null;
};

export function useEditor(widget: EditorWidget | null) {
  const [editorTab, setEditorTab] = useState<'preview' | 'published'>('preview');
  const [settingsDraft, setSettingsDraft] = useState<Settings | null>(null);
  const settingsDraftDeferred = useDebounce(settingsDraft, 200);

  const { data: mondayContext, error: mondayContextError } = useMondayContext();
  const { data: board } = useBoard(mondayContext?.boardId);

  const path = widget?.path ?? null;

  const {
    data: settingsServer,
    mutate: fetchServerSettings,
    error: settingsServerError,
  } = useSWR(
    board && path ? `/settings/${widget!.id}` : null,
    async () => {
      const boardInfo = boardToBoardInfo(board!);
      const result = await getSettings(path!);
      const defaultSettings = getDefaultSettings(boardInfo);
      const demoSettings = getDemoSettings(boardInfo);

      return result ? fixSettings({ ...defaultSettings, ...result }, boardInfo) : demoSettings || defaultSettings;
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const { data: widgetInfo, mutate: fetchWidgetInfo } = useSWR(
    path ? `/widget-info/${widget!.id}` : null,
    async () => {
      return await getWidgetInfo(path!);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const { data: executionsInfo } = useSWR(
    path ? `/executions` : null,
    async () => {
      let pId = 'large';

      try {
        const subscription = await getSubscription(mondayClient);
        pId = subscription?.plan_id || 'large';
      } catch (err) {
        console.log('could not fetch subscription', err);
      }

      return await getExecutions(pId);
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
    },
  );

  const { trigger: triggerSaveSettings, isMutating: isSavingSettings } = useSWRMutation(`/save-settings/${widget?.id}`, async () => {
    if (!path || !settingsDraftDeferred) {
      return;
    }

    const result = await saveSettings(path, settingsDraftDeferred);

    // saveSettings answers with null on a 404, so a missing widget has to be caught here as well as
    // through `error` - otherwise reading `.error` off null throws inside the mutation
    if (!result || result.error) {
      enqueueSnackbar(extractErrorMessage(result), { variant: 'error' });
      await fetchServerSettings();
      setSettingsDraft(null);
      return;
    }

    // the draft dies with the editor when the user goes back to the widget list, and this cache
    // entry is never revalidated - so what was just written has to become the cached server state.
    // otherwise re-opening the widget shows the settings from before the edit, and the next change
    // writes that stale blob back over them.
    await fetchServerSettings(settingsDraftDeferred, { revalidate: false });
  });

  const { trigger: triggerUnpublish, isMutating: isUnpublishing } = useSWRMutation(`/unpublish/${widget?.id}`, async () => {
    if (!path) {
      return;
    }

    setEditorTab('preview');
    return unpublishWidget(path);
  });

  useEffect(() => {
    if (settingsDraftDeferred) {
      triggerSaveSettings();
    }
  }, [settingsDraftDeferred]);

  // a draft belongs to the widget it was started on - dropping it on switch stops one widget's
  // unsaved changes from being written to another
  useEffect(() => {
    setSettingsDraft(null);
    setEditorTab('preview');
  }, [widget?.id]);

  async function resetSettings() {
    if (!path) {
      return;
    }

    // resetting the look of a widget must not rename it
    const defaultSettings = { ...getDefaultSettings(viewerData.boardInfo), name: settings?.name };
    await saveSettings(path, defaultSettings);
    setSettingsDraft(null);
    fetchServerSettings();
  }

  const settings = settingsDraft || settingsServer;

  const {
    loadingMessage,
    progress: itemLoaderProgress,
    data: viewerData,
    error: itemLoaderError,
  } = useItemLoader(
    board && settings ? settings : undefined,
    board && settings ? board.id : undefined,
    board && settings ? getNormalizedBoardFiltersGraphQL(board, settings?.boardFilters as any) : undefined,
  );

  function setSetting<K extends keyof Settings>(key: K, value: Settings[K]) {
    if (!settings) {
      return;
    }

    const newSettings: Settings = {
      ...settings,
      [key]: value,
    };

    setSettingsDraft(newSettings);
    setEditorTab('preview');
  }

  const isPublished = !!widgetInfo?.publishedAt;

  return {
    widget,
    setSettingsDraft,
    resetSettings,
    settings,
    setSetting,
    editorTab,
    setEditorTab,
    isSavingSettings,
    loadingMessage,
    itemLoaderProgress,
    viewerData,
    widgetInfo,
    fetchWidgetInfo,
    isPublished,
    triggerUnpublish,
    isUnpublishing,
    path,
    board,
    mondayContext,
    mondayContextError,
    settingsServerError,
    executionsInfo,
    itemLoaderError,
  };
}
